import React from 'react';
import ReportPage from '../ReportChart/ReportPage';
import { getCompletedServiceAnalysis } from "services/analytics";
import useChartColors from '../ReportChart/hooks/useChartColors';

const CompletedServicesAnalysis = () => {
  const { customCaptions } = JSON.parse(sessionStorage.getItem("me")) || JSON.parse(localStorage.getItem("me"));
  const { getAllColors } = useChartColors();

  const chartConfig = {
    id: 'completed-services-analysis',
    title: `Completed ${customCaptions?.servicePlural} Analysis`,
    renderCustomAxisTick: true,
    drilldownLabel: (dataPoint) => dataPoint 
      ? `${dataPoint?.payload?.label}`
      : `${customCaptions.versionPlural || 'Versions'}`,
    fetchData: async (params) => {
      // Clean up params - remove empty occurrence values
      if (params.occurrence === '' || params.occurrence === undefined) {
        delete params.occurrence;
      } else {
        // Convert occurrence to number if it's a string
        params.occurrence = parseInt(params.occurrence);
      }
      
      // Map roleId to modelVersionRoleId because that's what the API expects
      if (params.roleId) {
        params.modelVersionRoleId = params.roleId;
      }
      
      return await getCompletedServiceAnalysis(params);
    },
    allowedChartTypes: ['horizontal-bar', 'vertical-bar'],
    defaultChartType: 'horizontal-bar',
    axisConfig: {
      categoryAxis: { dataKey: 'label' },
      valueAxes: [
        { 
          label: 'Actual Minutes'
        },
        { 
          alignment: 'right',
          label: 'Estimated Minutes'
        }
      ]
    },
    series: [
      { 
        label: 'Estimated Minutes', 
        key: 'estimatedMinutes', 
        colors: [getAllColors()[0]], 
        axisLabel: 'Estimated Minutes',
      },
      { 
        label: 'Actual Minutes', 
        key: 'actualMinutes', 
        colors: [getAllColors()[1]], 
        axisLabel: 'Actual Minutes',
      },
    ],
    transformData: (response) => {
      if (!response?.data?.data) return [];
      
      return response.data.data.map(item => {
        // Format the scheduled date
        const scheduledDate = item.scheduledDate ? new Date(item.scheduledDate) : null;
        const formattedDate = scheduledDate 
          ? scheduledDate.toLocaleDateString('en-GB', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric'
            })
          : 'Unknown Date';
				// Combine available model and asset names into the first line,
				// then append model name and formatted date as separate lines
				// to create a multi-line label (skipping any missing values).
				const firstLine = [item?.modelModel, item?.assetName]
					.filter(Boolean)
					.join(' ');

				const multiLineLabel = [firstLine, item.modelName, formattedDate]
					.filter(Boolean)
					.join('\n');
        return {
          id: item.serviceID,
          label: multiLineLabel,
          rawData: item, // Keep the original data for reference if needed
          actualMinutes: parseFloat(item.actualMinutes || 0),
          estimatedMinutes: parseFloat(item.estimatedMinutes || 0),
          depth: 'V'
        };
      });
    },
    filters: {
      timeframe: {
        allowCustomRange: true,
        defaultRange: 'show-all',
      },
      department: { multiSelect: true },
      model: {
        showAll: true
      },
      asset: {
        show: model => model?.modelTemplateType === 'A'
      },
      interval: true,
      role: true,
      occurrence: {
        defaultValue: 100
      }
    },
    drilldown: {
      allowedChartTypes: ['vertical-bar', 'horizontal-bar'],
      defaultChartType: 'vertical-bar',
      drilldownLabel: (dataPoint) => dataPoint 
      ? `${dataPoint?.payload?.label}`
      : `${customCaptions.stagesPlural || 'Stages'}`,
      getParams: (dataPoint, currentParams = {}) => {
        if (!dataPoint) return currentParams;

        return {
          ...currentParams,
          serviceId: dataPoint.rawData?.serviceID || dataPoint.id
        };
      },
      transformData: (response) => {
        if (!response?.data?.data) return [];
        
        return response.data.data.map(item => {
          return {
            id: item.id || item.stageId,
            label: item.name,
            rawData: item, // Keep the original data for reference if needed
            actualMinutes: parseFloat(item.actualMinutes || 0),
            estimatedMinutes: parseFloat(item.estimatedMinutes || 0),
            depth: response.data.depth,
            hasZones: item.hasZones
          };
        });
      },
      drilldown: {
        allowedChartTypes: ['horizontal-bar', 'vertical-bar'],
        defaultChartType: 'horizontal-bar',
        drilldownLabel: (dataPoint, parentDataPoints) => {
          if (dataPoint) {
            return `${dataPoint?.payload?.label}`;
          }
          
          // Check if the parent dataPoint has hasZones=true
          const parentDataPoint = parentDataPoints && parentDataPoints.length > 0 ? parentDataPoints[parentDataPoints.length - 1] : null;
          
          if (parentDataPoint && parentDataPoint.hasZones === false) {
            return `${customCaptions.tasksPlural || 'Tasks'}`;
          }
          
          return `${customCaptions.zonesPlural || 'Zones'}`;
        },
        disableDrilldown: (parentDataPoints) => {
          // Disable further drilldown if the parent has hasZones=false
          const parentDataPoint = parentDataPoints && parentDataPoints.length > 0 ? parentDataPoints[parentDataPoints.length - 1] : null;
          return parentDataPoint && parentDataPoint.hasZones === false;
        },
        axisConfig: {
          categoryAxis: { dataKey: 'label', type: 'category', width: 200 },
          valueAxes: [{ label: 'Minutes' }] 
        },
        getParams: (dataPoint, currentParams = {}) => {
          if (!dataPoint) return currentParams;
        
          return {
            ...currentParams,
            modelVersionStageId: dataPoint.id || dataPoint.rawData?.id
          };
        },
        drilldown: {
          allowedChartTypes: ['horizontal-bar', 'vertical-bar'],
          defaultChartType: 'horizontal-bar',
          drilldownLabel: `${customCaptions.tasksPlural || 'Tasks'}`,
          axisConfig: {
            categoryAxis: { dataKey: 'label', type: 'category', width: 200 },
            valueAxes: [{ label: 'Minutes' }] 
          },
          series: [
            { 
              label: 'Estimated Minutes', 
              key: 'estimatedMinutes', 
              colors: [getAllColors()[0]],
            },
            { 
              label: 'Actual Minutes', 
              key: 'actualMinutes', 
              colors: [getAllColors()[1]],
              },
          ],
          getParams: (dataPoint, currentParams = {}) => {
            if (!dataPoint) return currentParams;
          
            return {
              ...currentParams,
              modelVersionZoneId: dataPoint.id || dataPoint.rawData?.id
            };
          },
        }
      }
    }
  };

  return (
    <ReportPage 
      title={`Completed ${customCaptions?.servicePlural} Analysis`}
      chartConfig={chartConfig}
    />
  );
};

export default CompletedServicesAnalysis;
